/**
 * ReportContentSubmitStep
 *
 * Final step of the ReportContentSheet stepper.
 * Displays the selected report item's title, item description,
 * a free-text comment field, and a Submit button.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { InputLengthFormatters } from '@/presentation/constants/formatters/inputLengthFormatters';
import type { ContentReportItemEntity } from '@/domain/entities/report/contentReportItemEntity';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { InputText } from '@/presentation/components/common/InputText';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ReportContentSubmitStepProps {
  submitItem: ContentReportItemEntity | null;
  comment: string;
  isSubmitting: boolean;
  onCommentChange: (value: string) => void;
  onSubmit: () => void;
}

export const ReportContentSubmitStep: React.FC<ReportContentSubmitStepProps> = ({
  submitItem,
  comment,
  isSubmitting,
  onCommentChange,
  onSubmit,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        paddingLeft: responsive.spacing.xl,
        paddingRight: responsive.spacing.xl,
        paddingTop: responsive.spacing.lg,
        paddingBottom: responsive.spacing.lg,
      }}
    >

      {/* Selected report item title */}
      {submitItem && (
        <p
          style={{
            margin: 0,
            marginBottom: responsive.spacing.base,
            color: theme.text.primary,
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.semibold,
          }}
        >
          {submitItem.title}
        </p>
      )}

      {/* Report item description */}
      {submitItem?.description && (
        <p
          style={{
            margin: 0,
            marginBottom: responsive.spacing['2xl'],
            color: theme.text.secondary,
            fontSize: responsive.font.base,
            lineHeight: 1.5,
          }}
        >
          {submitItem.description.replace(/\\n/g, '\n')}
        </p>
      )}

      {/* Comment text field */}
      <div
        style={{
          backgroundColor: theme.secondary,
          borderRadius: responsive.borderRadius.base,
          marginBottom: responsive.spacing['2xl'],
          overflow: 'hidden',
        }}
      >
        <InputText
          value={comment}
          onChange={onCommentChange}
          maxLength={InputLengthFormatters.reportMaxLength}
          placeholder={l10n.reportContent.commentPlaceholder}
          backgroundColor="transparent"
          height={responsive.form.textareaHeight}
          padding={responsive.spacing.base}
          style={{ width: '100%', boxSizing: 'border-box' }}
          qa={{ id: 'report-content-comment' }}
        />
      </div>

      {/* Spacer to push button toward bottom */}
      <div style={{ flex: 1 }} />

      {/* Submit button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          paddingBottom: responsive.spacing.lg,
        }}
      >
        <button
          {...qaAttr({ id: 'report-content-submit' })}
          onClick={onSubmit}
          disabled={isSubmitting}
          style={{
            backgroundColor: theme.danger,
            color: theme.white,
            border: 'none',
            borderRadius: responsive.button.borderRadius.sm,
            padding: responsive.button.padding.xl,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.semibold,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {l10n.reportContent.submit}
        </button>
      </div>

      {/* Submission overlay — shown on top of content while the request is in flight */}
      {isSubmitting && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: theme.modalOverlay,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ExpauseLoadingIndicator />
        </div>
      )}
    </div>
  );
};
